import React, { useState, useRef } from 'react';
import { Upload, X, Image, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ 
  value, 
  onChange, 
  uploadEndpoint = '/api/upload/image/article',
  label = 'Image',
  accept = 'image/*',
  maxSize = 5,
  previewHeight = 'h-40'
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`Le fichier est trop volumineux (max ${maxSize}MB)`);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}${uploadEndpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'upload');
      }

      // Return full URL
    const imageUrl = data.url.startsWith('http') 
      ? data.url  // URL complète Cloudinary
      : `${API_URL}${data.url}`; // URL relative (ancien système)

    onChange(imageUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  const isImage = accept.includes('image');

  return (
    <div className="space-y-2">
      <label className="block text-white/60 text-sm">{label}</label>
      
      {!value ? (
        // Upload zone
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-none p-8 text-center cursor-pointer
            transition-all duration-200
            ${dragOver 
              ? 'border-accent bg-accent/10' 
              : 'border-white/20 hover:border-white/40 bg-white/[0.02]'
            }
            ${uploading ? 'pointer-events-none' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-accent animate-spin" />
              <p className="text-white/60 text-sm">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              {isImage ? (
                <Image className="w-10 h-10 text-white/40" />
              ) : (
                <FileText className="w-10 h-10 text-white/40" />
              )}
              <div>
                <p className="text-white/60 text-sm">
                  <span className="text-accent">Cliquez pour choisir</span> ou glissez-déposez
                </p>
                <p className="text-white/40 text-xs mt-1">
                  {isImage ? 'JPG, PNG, WebP, GIF' : 'PDF, XLSX, DOCX, PPTX, ZIP'} • Max {maxSize}MB
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Preview
        <div className="relative">
          {isImage ? (
            <img 
              src={value} 
              alt="Aperçu" 
              className={`w-full max-w-md ${previewHeight} object-cover border border-white/10`}
              onError={(e) => { 
                e.target.style.display = 'none';
                setError('Impossible de charger l\'image');
              }}
            />
          ) : (
            <div className="flex items-center gap-3 p-4 bg-white/5 border border-white/10">
              <FileText className="w-8 h-8 text-accent" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{value.split('/').pop()}</p>
                <p className="text-white/40 text-xs">Fichier uploadé</p>
              </div>
            </div>
          )}
          
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-500 text-white p-1.5 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}

      {/* Alternative: URL input */}
      {!value && !uploading && (
        <div className="mt-3">
          <p className="text-white/40 text-xs mb-2">Ou entrez une URL :</p>
          <input
            type="url"
            placeholder="https://..."
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-accent"
          />
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
